import FurnitureNavbar from '../../../components/layout/Nav';
import './admindashboard.scss';
import { useEffect, useState } from 'react';
import withAuth from '../../auth/WithAuth';
import { serverUrlAuth } from '../../../serverUrl';
import {
  FaUsers,
  FaProjectDiagram,
  FaSpinner,
  FaDollarSign,
  FaHandshake,
  FaFileAlt,
  FaChartLine,
  FaWallet,
  FaStore,
  FaCreditCard,
  FaShoppingCart,
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
);

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchDashboardData = async () => {
    try {
      const response = await serverUrlAuth.get('projects/admin/dashboard/');
      setDashboardData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching admin dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return `KSh ${(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <>
        <FurnitureNavbar />
        <div className="admin-dashboard loading">
          <div className="loading-container">
            <FaSpinner className="spinner" />
            <p>Loading dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  const data = dashboardData || {};

  // Chart: Revenue Overview (Bar Chart) - Now with Total bar
  const revenueChartData = {
    labels: ['Custom Projects', 'Ready-Made Shop', 'Total Platform Revenue'],
    datasets: [
      {
        label: 'Revenue (KSh)',
        data: [
          data.revenue?.custom_projects?.total || 0,
          data.revenue?.ready_made?.total || 0,
          data.revenue?.platform?.total || 0,
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(103, 50, 0, 0.8)',
        ],
        borderColor: ['#3b82f6', '#10b981', '#673200'],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `KSh ${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return 'KSh ' + value.toFixed(2);
          },
        },
      },
    },
  };

  // Chart: Project Status Distribution (Doughnut)
  const projectStatusData = {
    labels: ['Open', 'In Progress', 'Awaiting Deposit', 'Completed'],
    datasets: [
      {
        data: [
          data.projects?.open || 0,
          data.projects?.in_progress || 0,
          data.projects?.awaiting_deposit || 0,
          data.projects?.completed || 0,
        ],
        backgroundColor: [
          'rgba(245, 158, 11, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
        ],
        borderColor: ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981'],
        borderWidth: 2,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
    },
    cutout: '65%',
  };

  // Chart: Monthly Revenue Trend (Line Chart) - Using actual monthly data from API
  const monthlyRevenueData = {
    labels: data.monthly_revenue?.labels || [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    datasets: [
      {
        label: 'Total Revenue',
        data: data.monthly_revenue?.total || Array(12).fill(0),
        borderColor: '#673200',
        backgroundColor: 'rgba(103, 50, 0, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#673200',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Custom Projects',
        data: data.monthly_revenue?.custom_projects || Array(12).fill(0),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Ready-Made',
        data: data.monthly_revenue?.ready_made || Array(12).fill(0),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: KSh ${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return 'KSh ' + value.toFixed(2);
          },
        },
      },
    },
  };

  // Payment Status Chart - Using payments data for custom projects
  const paymentStatusData = {
    labels: ['Successful', 'Pending', 'Abandoned', 'Failed'],
    datasets: [
      {
        data: [
          data.payments?.summary?.successful || 0,
          data.payments?.summary?.pending || 0,
          data.payments?.summary?.abandoned || 0,
          data.payments?.summary?.failed || 0,
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(107, 114, 128, 0.8)',
        ],
        borderColor: ['#10b981', '#f59e0b', '#ef4444', '#6b7280'],
        borderWidth: 2,
      },
    ],
  };

  const paymentDoughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
    },
    cutout: '65%',
  };

  // Checkout Status Chart - Using checkout data for ready-made products
  const checkoutStatusData = {
    labels: ['Paid', 'Pending Payment', 'Failed', 'Refunded'],
    datasets: [
      {
        data: [
          data.checkout?.summary?.paid || 0,
          data.checkout?.summary?.pending_payment || 0,
          data.checkout?.summary?.failed || 0,
          data.checkout?.summary?.refunded || 0,
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(107, 114, 128, 0.8)',
        ],
        borderColor: ['#10b981', '#f59e0b', '#ef4444', '#6b7280'],
        borderWidth: 2,
      },
    ],
  };

  // Payment Type Breakdown Chart - Bar Chart showing deposit vs balance
  const paymentTypeData = {
    labels: ['Deposit', 'Balance'],
    datasets: [
      {
        label: 'Number of Payments',
        data: [
          data.payments?.payment_type_breakdown?.deposit || 0,
          data.payments?.payment_type_breakdown?.balance || 0,
        ],
        backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)'],
        borderColor: ['#3b82f6', '#10b981'],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const paymentTypeOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.parsed.y} payments`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Top stats for quick view
  const topStats = [
    {
      icon: FaUsers,
      label: 'Total Users',
      value: data.users?.total || 0,
      subtitle: `${data.users?.new_today || 0} new today`,
      color: '#3b82f6',
      bg: 'rgba(59, 130, 246, 0.1)',
    },
    {
      icon: FaProjectDiagram,
      label: 'Total Projects',
      value: data.projects?.total || 0,
      subtitle: `${data.projects?.new_this_month || 0} this month`,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.1)',
    },
    {
      icon: FaDollarSign,
      label: 'Total Revenue',
      value: formatCurrency(data.revenue?.platform?.total || 0),
      subtitle: formatCurrency(data.revenue?.platform?.today || 0) + ' today',
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.1)',
    },
    {
      icon: FaStore,
      label: 'Total Orders',
      value: data.checkout?.summary?.total_orders || 0,
      subtitle: `${data.checkout?.summary?.new_today || 0} new today`,
      color: '#8b5cf6',
      bg: 'rgba(139, 92, 246, 0.1)',
    },
    {
      icon: FaHandshake,
      label: 'Negotiations',
      value: data.negotiations?.total || 0,
      subtitle: `${data.negotiations?.open || 0} open`,
      color: '#ec4899',
      bg: 'rgba(236, 72, 153, 0.1)',
    },
    {
      icon: FaFileAlt,
      label: 'Proposals',
      value: data.proposals?.total || 0,
      subtitle: `${data.proposals?.selected || 0} selected`,
      color: '#14b8a6',
      bg: 'rgba(20, 184, 166, 0.1)',
    },
  ];

  // Payment type breakdown
  const paymentTypeBreakdown = data.payments?.payment_type_breakdown || {};
  const hasPaymentData = Object.keys(paymentTypeBreakdown).length > 0;

  return (
    <>
      <FurnitureNavbar />
      <div className="admin-dashboard">
        {/* Header */}
        <header className="admin-header">
          <div className="container">
            <div className="header-content">
              <div className="header-text">
                <h1>
                  <span className="header-icon">📊</span> Admin Dashboard
                </h1>
                <p>Monitor platform performance and manage your marketplace</p>
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          {/* Quick Stats Row */}
          <div className="quick-stats">
            {topStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="quick-stat">
                  <div
                    className="quick-stat-icon"
                    style={{ background: stat.bg, color: stat.color }}
                  >
                    <Icon />
                  </div>
                  <div className="quick-stat-info">
                    <span className="quick-stat-label">{stat.label}</span>
                    <span className="quick-stat-value">{stat.value}</span>
                    {stat.subtitle && (
                      <span className="quick-stat-subtitle">
                        {stat.subtitle}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Revenue Overview Cards */}
          <div className="revenue-overview-cards">
            <div className="revenue-card custom">
              <div className="revenue-card-icon">
                <FaProjectDiagram />
              </div>
              <div className="revenue-card-info">
                <span className="revenue-card-label">
                  Custom Projects Revenue
                </span>
                <span className="revenue-card-value">
                  {formatCurrency(data.revenue?.custom_projects?.total || 0)}
                </span>
                <span className="revenue-card-sub">
                  {formatCurrency(data.revenue?.custom_projects?.monthly || 0)}{' '}
                  this month
                </span>
              </div>
            </div>
            <div className="revenue-card ready">
              <div className="revenue-card-icon">
                <FaStore />
              </div>
              <div className="revenue-card-info">
                <span className="revenue-card-label">Ready-Made Revenue</span>
                <span className="revenue-card-value">
                  {formatCurrency(data.revenue?.ready_made?.total || 0)}
                </span>
                <span className="revenue-card-sub">
                  {formatCurrency(data.revenue?.ready_made?.monthly || 0)} this
                  month
                </span>
              </div>
            </div>
            <div className="revenue-card total">
              <div className="revenue-card-icon">
                <FaDollarSign />
              </div>
              <div className="revenue-card-info">
                <span className="revenue-card-label">
                  Total Platform Revenue
                </span>
                <span className="revenue-card-value">
                  {formatCurrency(data.revenue?.platform?.total || 0)}
                </span>
                <span className="revenue-card-sub">
                  {formatCurrency(data.revenue?.platform?.today || 0)} today
                </span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="charts-row">
            {/* Revenue Chart */}
            <div className="chart-card revenue-chart">
              <div className="chart-header">
                <h3>
                  <FaDollarSign className="chart-icon" /> Revenue Overview
                </h3>
                <span className="chart-subtitle">
                  Total platform revenue breakdown
                </span>
              </div>
              <div className="chart-container">
                <Bar data={revenueChartData} options={revenueChartOptions} />
              </div>
            </div>

            {/* Project Status Chart */}
            <div className="chart-card status-chart">
              <div className="chart-header">
                <h3>
                  <FaProjectDiagram className="chart-icon" /> Project Status
                </h3>
                <span className="chart-subtitle">Distribution by status</span>
              </div>
              <div className="chart-container doughnut-container">
                <Doughnut data={projectStatusData} options={doughnutOptions} />
              </div>
            </div>
          </div>

          {/* Second Row of Charts */}
          <div className="charts-row">
            {/* Monthly Revenue Trend - Now with 3 datasets */}
            <div className="chart-card trend-chart">
              <div className="chart-header">
                <h3>
                  <FaChartLine className="chart-icon" /> Monthly Revenue Trend
                </h3>
                <span className="chart-subtitle">
                  Revenue performance over time
                </span>
              </div>
              <div className="chart-container">
                <Line data={monthlyRevenueData} options={lineOptions} />
              </div>
            </div>

            {/* Payment Type Breakdown Chart - Bar Chart */}
            {hasPaymentData && (
              <div className="chart-card payment-type-chart">
                <div className="chart-header">
                  <h3>
                    <FaCreditCard className="chart-icon" /> Payment Type
                    Breakdown
                  </h3>
                  <span className="chart-subtitle">
                    Deposit vs Balance payments
                  </span>
                </div>
                <div className="chart-container">
                  <Bar data={paymentTypeData} options={paymentTypeOptions} />
                </div>
              </div>
            )}
          </div>

          {/* Third Row of Charts */}
          <div className="charts-row">
            {/* Checkout Status Chart - Ready-Made Products */}
            <div className="chart-card status-chart">
              <div className="chart-header">
                <h3>
                  <FaShoppingCart className="chart-icon" /> Checkout Status
                </h3>
                <span className="chart-subtitle">Ready-made order status</span>
              </div>
              <div className="chart-container doughnut-container">
                <Doughnut
                  data={checkoutStatusData}
                  options={paymentDoughnutOptions}
                />
              </div>
            </div>

            {/* Payment Status Chart - Custom Projects */}
            <div className="chart-card status-chart">
              <div className="chart-header">
                <h3>
                  <FaWallet className="chart-icon" /> Payment Status
                </h3>
                <span className="chart-subtitle">
                  Custom projects payment status
                </span>
              </div>
              <div className="chart-container doughnut-container">
                <Doughnut
                  data={paymentStatusData}
                  options={paymentDoughnutOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(AdminDashboard, 'admin');
